<script lang="ts" setup>
import { cva } from 'class-variance-authority';
import { computed, ref } from 'vue';
import { cn } from '@/helpers/tailwind-helpers';
import LocalIcon, { LocalIconProps } from '@/atoms/LocalIcon.vue';
import Label from '@/atoms/BaseLabel.vue';
import { RouterLink } from 'vue-router';

export type ButtonState = 'default' | 'pressed' | 'disabled' | 'loading';

const ACTIVE_STATES = ['default', 'pressed'];

export type BaseButtonProps = {
  id?: string;
  type?:
    | 'primary'
    | 'secondary'
    | 'white'
    | 'ghost'
    | 'brand'
    | 'success'
    | 'destructive'
    | 'destructive_subtle';
  size?: 'small' | 'medium';
  class?: string | { [prop: string]: string | boolean };
  state?: ButtonState;
  iconName?: string;
  icon?: LocalIconProps;
  label?: string;
  secondIconName?: string;
  onClick?: () => Promise<any> | void;
  to?: string | Record<string, any>;
  isLoading?: boolean;
};

const baseButton = cva(
  'bg-brand-solid flex flex-row items-center justify-center rounded-full py-2 space-x-2 fill-brand-solid text-brand-solid',
  {
    variants: {
      size: {
        small: 'min-h-8 max-h-8 px-3 text-sm font-medium leading-5',
        medium: 'min-h-13 max-h-13 px-4 font-medium leading-6',
      },
      type: {
        primary: 'fill-white bg-brand-solid text-white',
        secondary: 'bg-secondary',
        brand: 'bg-blue-50',
        white: 'bg-white',
        ghost: 'bg-transparent',
        destructive: 'bg-error-solid fill-white text-white',
        destructive_subtle: 'bg-error-primary text-error-primary fill-error-primary',
        success: 'bg-success',
      },
      state: {
        default: '',
        disabled: 'bg-grey-200 text-grey-500 fill-grey-500',
        pressed: '',
        loading: '',
      },
      display: {
        default: '',
        icon: 'p-2',
      },
    },
    compoundVariants: [
      { type: 'primary', state: 'pressed', class: 'bg-blue-900' },
      { type: 'secondary', state: 'pressed', class: 'bg-grey-100' },
      { type: 'white', state: 'pressed', class: 'bg-grey-25' },
      { type: 'ghost', state: 'pressed', class: 'bg-grey-25' },
      { type: 'destructive', state: 'pressed', class: 'bg-error-hover' },
      { type: 'destructive_subtle', state: 'pressed', class: 'bg-error-primary-hover' },

      { display: 'icon', size: 'medium', class: 'p-4' },
    ],
    defaultVariants: {
      size: 'medium',
      type: 'primary',
      state: 'default',
    },
  },
);

const props = withDefaults(defineProps<BaseButtonProps>(), {
  state: 'default',
});

const pressed = ref(false);
const asyncLoading = ref(false);

const classes = computed(() =>
  cn(
    baseButton({
      ...props,
      state: asyncLoading.value ? 'loading' : pressed.value ? 'pressed' : props.state,
      display: !props.label && !props.secondIconName ? 'icon' : 'default',
    }),
    props.class,
  ),
);

const setPressed = (newPressed: boolean) => {
  const activeStates = ['default', 'pressed'];
  if (activeStates.includes(props.state)) {
    pressed.value = newPressed;
  }
};

const handleClick = async (e: Event) => {
  if (!ACTIVE_STATES.includes(props.state) || asyncLoading.value) {
    e.preventDefault();
    return;
  }
  if (!props.to) e.preventDefault();
  if (!props.onClick) return;
  const result = props.onClick();
  if (result instanceof Promise) {
    asyncLoading.value = true;
    try {
      await result;
    } finally {
      asyncLoading.value = false;
    }
  }
};
</script>

<template>
  <component
    :data-cy="id"
    :is="to ? RouterLink : 'button'"
    :to="state === 'disabled' ? undefined : to"
    :aria-disabled="state === 'disabled' || asyncLoading"
    @click="handleClick"
    :class="classes"
    @mousedown="setPressed(true)"
    @mouseup="setPressed(false)"
    @pointerdown="setPressed(true)"
    @pointerup="setPressed(false)"
  >
    <template v-if="state !== 'loading' && !isLoading && !asyncLoading">
      <LocalIcon :size="size" v-if="iconName" :name="iconName" />
      <LocalIcon v-else-if="icon" v-bind="icon" />
      <Label v-if="label" :preset="size" :text="label" weight="medium" />
      <LocalIcon :size="size" v-if="secondIconName" :name="secondIconName" />
    </template>
    <LocalIcon size="large" class="animate-spin" v-else name="loader-circle" />
  </component>
</template>
